/* eslint-disable no-unused-vars */
import { store } from 'redux/store';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import React, { useEffect, useMemo, useState, useRef } from 'react';
import _ from 'lodash';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import moment from 'moment';

import LoadingRound from '@components/loading-round/loading-round';
import SaveModal from '@components/modal/variants/save-modal';
import Header from './header';
import BodyContainer from './body-container';
import Footer from './footer';
import DeleteModal from '@components/modal/variants/delete-modal';

import { equipmentMobileData } from 'web-workers/helper';
import {
  getEquipmentDetailsWithVesselsAndPSVsAndItsSiteDetails,
  transformEditEquipmentDataForUpdate,
  revertEditEquipmentData,
  transformAddOrEditNewEquipmentDataForUpdate,
  attachedNewPSVToVesselEquipment,
  revertPSVs,
  setDefaultValuesOnAddOrEditEquipmentForm,
  revertNewEquipmentJurisdictionNumbersListAndMap,
  getParentKeys
} from './helper';
import { getAppDispatch } from '@utilities/dispatch.util';
import { useToastContext } from '@context/toaster.context';
import { jurisdictionNumbersActions } from '@redux/slices/jurisdiction-numbers';
import { cacheActions } from '@redux/slices/cache';

import style from './style.module.scss';
import AddPSVModal from '@components/modal/variants/psv-and-vessels-modal';
import AddNewPSVEquipment from '@components/app-bar/add-new-wizards/add-new-psv-equipment';
import AppBar from '@components/app-bar';
import PSVEquipmentModal from './psv-equipment-modal';
import { pvPsvActions } from '@redux/slices/pv-psv';
import InspectionService from '@services/inspection.service';
import ChecksService from '@services/checks.service';
import WorkOrderService from '@services/workorder.service';
import AttachPSVModal from '@components/modal/variants/attach-psv-modal';
import { getEquipmentTypeVersion } from './vessels-psvs-info';
import AddFieldsModal from '@pages/settings/add-fields-modal';
import { equipmentSelectedMoreInfoFieldsSchema } from './form-schema-and-default-value';
import { getDataFromObjects } from './form-fields';
import { settingsActions } from '@redux/slices/settings';
import { getDataPoints } from '@pages/settings/helper';
import DisabledLocation from '@components/location-toast';

const AddOrEditEquipment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = getAppDispatch();
  const { ToasterUtil } = useToastContext();

  const equipmentId = searchParams.get('equipmentId') || '';
  const siteId = searchParams.get('siteId') || '';
  const hideDiscardBtn = searchParams.get('hideDiscardBtn') || false;
  const newEquipmentId = searchParams.get('new-equipmentId') || '';
  const equipmentHeaderTitle = searchParams.get('title') || '';
  const equipmentIdThatAttachedToPSV = searchParams.get('equipmentIdThatAttachedToPSV') || '';
  const vesselEquipmentId = searchParams.get('vesselEquipmentId') || '';
  const isVesselUpdated = searchParams.get('isVesselUpdated') === 'true' ? true : false;
  const isOnlyPSVDataUpdatedForVesselId = searchParams.get('isOnlyPSVDataUpdatedForVesselId') || '';
  const isOnlyPSVDataUpdatedForVessel =
    searchParams.get('isOnlyPSVDataUpdatedForVessel') === 'true' ? true : false;

  const sitesDetails = searchParams.get('sitesDetails') || false;
  const isNewEquipment = searchParams.get('isNewEquipment') === 'true' ? true : false;
  const isEditable = equipmentId ? true : false;

  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  const [commentForReviewer, setCommentForReviewer] = useState('');
  const [equipmentData, setEquipmentData] = useState(null);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [disabledSaveBtn, setDisabledSaveBtn] = useState(false);
  const [resetEquipmentModel, setResetEquipmentModal] = useState(false);
  const [openAddPsvModal, setOpenAddPsvModal] = useState(false);
  const [openLinkToExistingPSVsModal, setOpenLinkToExistingPSVsModal] = useState(false);
  const [showAttachOrDeAttachPSVModalBeforeSaved, setShowAttachOrDeAttachPSVModalBeforeSaved] =
    useState(false);
  const [openAttachOrReattachModal, setOpenAttachOrReattachModal] = useState(false);
  const [isDetachedAndEquipmentId, setIsDetachedAndEquipmentId] = useState({
    isDetached: false,
    equipmentId: ''
  });
  const [resetNewAndAssignedEquipmentLoading, setResetNewAndAssignedEquipmentLoading] =
    useState(false);
  const [openAddFieldModal, setOpenAddFieldModal] = useState(false);

  useEffect(() => {
    if (equipmentId || siteId || newEquipmentId) {
      setLoading(true);
      reset({});
      getEquipmentDetails({
        equipmentId: equipmentId || newEquipmentId,
        siteId,
        equipmentIdThatAttachedToPSV
      });
    }
  }, [equipmentId, siteId, newEquipmentId, equipmentIdThatAttachedToPSV, vesselEquipmentId]);

  const getEquipmentDetails = async ({ equipmentId, siteId, equipmentIdThatAttachedToPSV }) => {
    try {
      const checkTheEquipmentIsUpdated = localStorage.getItem('equipmentDetailsUpdated') || '';
      if (
        checkTheEquipmentIsUpdated &&
        checkTheEquipmentIsUpdated !== equipmentId &&
        checkTheEquipmentIsUpdated !== newEquipmentId
      ) {
        localStorage.removeItem('equipmentDetailsUpdated');
        localStorage.removeItem('onlyPSVDataUpdatedForVessel');
      }
      const response = await getEquipmentDetailsWithVesselsAndPSVsAndItsSiteDetails({
        equipmentId,
        siteIdForNewEquipment: siteId,
        equipmentIdThatAttachedToPSV,
        vesselEquipmentId
      });
      reset({ ...response.initialFormFieldsValue });

      setCommentForReviewer(response.initialFormFieldsValue?.commentForReviewer || '');
      setEquipmentData(response);
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    } catch (error) {
      console.log({ error });
    }
  };

  const {
    siteDetail,
    formFields,
    equipmentDetails,
    formWrapperSchema,
    equipmentMoreFieldSchema,
    formWrapperDefaultValues,
    isVesselVersion,
    isPSVVersion,
    equipmentCategoryMap,
    equipmentAutoPopulateFields,
    attachAndDeAttach
  } = useMemo(() => {
    if (!loading && equipmentData) {
      return equipmentData;
    } else return {};
  }, [loading, equipmentData]);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    setValue,
    getValues,
    watch,
    reset,
    register,
    trigger
  } = useForm({
    resolver: yupResolver(
      // equipmentSelectedMoreInfoFieldsSchema
      yup.object().shape({
        ...equipmentSelectedMoreInfoFieldsSchema,
        ...formWrapperSchema,
        moreFields: yup.object().shape({
          ...equipmentMoreFieldSchema
        })
      })
    ),
    defaultValues: formWrapperDefaultValues
  });

  const showDeleteBtn = useMemo(() => {
    return hideDiscardBtn === 'true' ? true : false;
  }, [hideDiscardBtn]);

  const dirtyRef = useRef({
    isDirty: false,
    dirtyFields: {}
  });

  useEffect(() => {
    dirtyRef.current = {
      isDirty,
      dirtyFields
    };
  }, [isDirty, dirtyFields]);

  let blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      ((dirtyRef.current.isDirty && Object.keys(dirtyRef.current.dirtyFields)?.length > 0) ||
        dirtyRef.current.navigated) &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (dirtyRef.current.navigated) {
        blocker.proceed();
      } else {
        dirtyRef.current.isDirty && Object.keys(dirtyRef.current.dirtyFields)?.length
          ? setSaveInProgress(true)
          : closedModal();
      }
    }
  }, [blocker, dirtyRef]);

  const onSubmitHandler = async ({ data, status, actionComeFromButton = true, revert = false }) => {
    const onlyPSVDataUpdatedForVessel =
      localStorage.getItem('onlyPSVDataUpdatedForVessel') || false;
    let isChangingPSVVesselRelationship =
      store.getState()?.settings?.isChangingPSVVesselRelationship || false;
    if (
      actionComeFromButton &&
      onlyPSVDataUpdatedForVessel &&
      !isChangingPSVVesselRelationship &&
      !vesselEquipmentId &&
      !revert
    ) {
      actionComeFromButton && setShowAttachOrDeAttachPSVModalBeforeSaved(true);
    } else if (actionComeFromButton && onlyPSVDataUpdatedForVessel && !revert) {
      onClickOkEvent();
    } else {
      const submitData = getDataFromObjects({
        data
      });
      if (newEquipmentId) {
        if (revert)
          await resetNewAndAssignedEquipment({ equipmentId: newEquipmentId, newEquipment: true });
        else
          await addOrUpdateNewEquipment({
            data: submitData,
            newEquipmentId,
            status,
            actionComeFromButton
          });
      } else {
        if (revert) await resetNewAndAssignedEquipment({ equipmentId });
        else
          await updateAssignedEquipment({
            data: submitData,
            equipmentId,
            status,
            actionComeFromButton
          });
        setDisabledSaveBtn(false);
      }
    }
  };

  const addOrUpdateNewEquipment = async ({
    data,
    newEquipmentId,
    status,
    actionComeFromButton
  }) => {
    try {
      let isNewEquipmentAdded = localStorage.getItem('equipmentDetailsUpdated') ? true : false;

      const { transform, newJurisdictionNumber } = transformAddOrEditNewEquipmentDataForUpdate({
        data,
        equipmentId: newEquipmentId
      });

      if (!isNewEquipmentAdded) {
        const equipments = store.getState()?.cache?.equipments || [];

        let pvIds = [];
        if (equipmentIdThatAttachedToPSV) {
          pvIds.push({
            pvId: equipmentIdThatAttachedToPSV,
            psvId: newEquipmentId,
            // verificationStatus: 'attached'
            verificationStatus: 'new'
          });
        }

        const copyEquipments = [...equipments];
        copyEquipments.push({
          equipmentId: newEquipmentId,
          equipmentEditingStatus: status,
          updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
          isNew: true,
          siteId,
          pvIds,
          ...transform
        });

        const res = await equipmentMobileData({
          equipmentPayloadData: copyEquipments,
          isCheckOldAndNewArray: false
        });
        localStorage.setItem('equipmentDetailsUpdated', newEquipmentId);
        dispatch?.(cacheActions.setEquipments(res));

        if (newJurisdictionNumber)
          dispatch?.(
            jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
              ...newJurisdictionNumber
            })
          );

        if (equipmentIdThatAttachedToPSV) {
          const { pvPsv, pvPsvMap } = attachedNewPSVToVesselEquipment({
            equipmentId: equipmentIdThatAttachedToPSV,
            attachedPSVId: newEquipmentId
          });
          dispatch?.(pvPsvActions.setPVPSV({ pvPsv, pvPsvMap }));
        }
      } else {
        localStorage.setItem('equipmentDetailsUpdated', newEquipmentId);
        let transformCopy = _.cloneDeep(transform);
        dispatch?.(
          cacheActions.updateExistingEquipments({
            equipmentId: newEquipmentId,
            equipmentEditingStatus: status,
            updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
            isNew: true,
            ...transformCopy
          })
        );

        if (newJurisdictionNumber)
          dispatch?.(
            jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
              ...newJurisdictionNumber
            })
          );
      }

      if (actionComeFromButton) {
        localStorage.removeItem('equipmentDetailsUpdated');
        localStorage.removeItem('onlyPSVDataUpdatedForVessel');
        if (vesselEquipmentId && isVesselUpdated) {
          localStorage.setItem('equipmentDetailsUpdated', vesselEquipmentId);
        } else if (vesselEquipmentId && !isVesselUpdated) {
          localStorage.setItem('onlyPSVDataUpdatedForVessel', vesselEquipmentId);
        }
        if (isOnlyPSVDataUpdatedForVesselId && isOnlyPSVDataUpdatedForVessel) {
          localStorage.setItem('onlyPSVDataUpdatedForVessel', isOnlyPSVDataUpdatedForVesselId);
        }
        ToasterUtil.showSuccess(`Record saved as ${status}`);
        navigate(-1);
        dirtyRef.current = {
          isDirty: false,
          dirtyFields: {}
        };
        setSaveInProgress(false);
        setResetEquipmentModal(false);
        setResetNewAndAssignedEquipmentLoading(false);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const resetNewAndAssignedEquipment = async ({ equipmentId, newEquipment = false }) => {
    setResetNewAndAssignedEquipmentLoading(true);
    try {
      if (equipmentDetails?.isNew || newEquipment) {
        resetNewEquipment({ equipmentId });
      } else {
        resetAssignedEquipment({ equipmentId });
      }
    } catch (error) {
      console.log('error', { error });
      setResetNewAndAssignedEquipmentLoading(false);
    }
  };

  const resetNewEquipment = async ({ equipmentId }) => {
    await revertAllNewAndAssignedPSVs();

    let pvIds = equipmentDetails?.pvIds || [];
    pvIds = pvIds?.filter((x) => x?.pvId !== vesselEquipmentId);

    if (vesselEquipmentId && pvIds?.length > 0) {
      dispatch?.(
        cacheActions.updateExistingEquipments({
          ...equipmentDetails,
          pvIds: pvIds
        })
      );
    } else {
      const { inspection, checks, tasks: reduxTasks, cache } = store.getState();
      const { equipments, pendingSync } = cache;

      deleteAllTasksThatCreatedWithThisNewEquipment({
        pendingSync,
        inspection,
        checks,
        reduxTasks
      });

      const transformJurisdiction = revertNewEquipmentJurisdictionNumbersListAndMap({
        equipmentId
      });

      let copyEquipments = [...equipments];
      copyEquipments = copyEquipments?.filter((x) => x?.equipmentId !== equipmentId);
      const res = await equipmentMobileData({
        equipmentPayloadData: copyEquipments,
        isCheckOldAndNewArray: false
      });
      dispatch?.(cacheActions.setEquipments(res));
      if (transformJurisdiction)
        dispatch?.(
          jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
            ...transformJurisdiction
          })
        );
    }

    localStorage.removeItem('equipmentDetailsUpdated');
    localStorage.removeItem('onlyPSVDataUpdatedForVessel');
    ToasterUtil.showSuccess(`Record changes discarded`);
    if (vesselEquipmentId && isVesselUpdated) {
      localStorage.setItem('equipmentDetailsUpdated', vesselEquipmentId);
    } else if (vesselEquipmentId && !isVesselUpdated) {
      localStorage.setItem('onlyPSVDataUpdatedForVessel', vesselEquipmentId);
    }

    if (isOnlyPSVDataUpdatedForVesselId && isOnlyPSVDataUpdatedForVessel) {
      localStorage.setItem('onlyPSVDataUpdatedForVessel', isOnlyPSVDataUpdatedForVesselId);
    }
    navigate(isNewEquipment ? -2 : -1);
    dirtyRef.current = {
      isDirty: false,
      dirtyFields: {}
    };

    setSaveInProgress(false);
    setResetEquipmentModal(false);
    setResetNewAndAssignedEquipmentLoading(false);
  };

  const deleteAllTasksThatCreatedWithThisNewEquipment = ({
    pendingSync,
    inspection,
    checks,
    reduxTasks
  }) => {
    if (pendingSync > 0) {
      const inspectionList = inspection?.inspectionsList || [];
      const checksList = checks?.checksList || [];
      const tasksList = reduxTasks?.tasksList || [];

      const newEquipmentInspection = inspectionList
        ?.filter((x) => x?.equipmentId === equipmentDetails.equipmentId)
        ?.map((x) => x?.id);

      const newEquipmentChecks = checksList
        ?.filter((x) => x?.equipmentId === equipmentDetails.equipmentId)
        ?.map((x) => x?.id);

      const newEquipmentWorkOrder = tasksList
        ?.filter((x) => x?.equipmentId === equipmentDetails.equipmentId)
        ?.map((x) => x?.woId);

      for (let insId of newEquipmentInspection) InspectionService.deleteNewInspection(insId);
      for (let checkId of newEquipmentChecks) ChecksService.deleteNewCheck({ checkId });
      for (let workOrderId of newEquipmentWorkOrder)
        WorkOrderService.deleteWorkOrderAndRequest(workOrderId);
    }
  };

  const resetAssignedEquipment = async ({ equipmentId }) => {
    await revertAllNewAndAssignedPSVs();

    const { transform, transformJurisdiction } = revertEditEquipmentData({
      equipmentId: equipmentId
    });

    let transformCopy = _.cloneDeep(transform);

    if (vesselEquipmentId) {
      let pvIds = equipmentDetails?.pvIds || [];
      pvIds = pvIds?.filter((x) => x?.pvId !== vesselEquipmentId);

      const resetFull = pvIds?.length > 0 ? false : true;

      dispatch?.(
        cacheActions.updateExistingEquipments({
          ...transformCopy,
          equipmentId: equipmentId,
          equipmentEditingStatus: resetFull ? '' : equipmentDetails.equipmentEditingStatus,
          verificationStatus: resetFull ? '' : equipmentDetails?.verificationStatus || '',
          pvIds: resetFull ? [] : pvIds
        })
      );

      if (transformJurisdiction)
        dispatch?.(
          jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
            ...transformJurisdiction
          })
        );
    } else {
      dispatch?.(
        cacheActions.updateExistingEquipments({
          ...transformCopy,
          equipmentId: equipmentId,
          equipmentEditingStatus: '',
          verificationStatus: equipmentDetails?.verificationStatus || '',
          pvIds: []
        })
      );

      if (transformJurisdiction)
        dispatch?.(
          jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
            ...transformJurisdiction
          })
        );
    }

    localStorage.removeItem('equipmentDetailsUpdated');
    localStorage.removeItem('onlyPSVDataUpdatedForVessel');
    ToasterUtil.showSuccess(`Record changes discarded`);
    if (vesselEquipmentId && isVesselUpdated) {
      localStorage.setItem('equipmentDetailsUpdated', vesselEquipmentId);
    } else if (vesselEquipmentId && !isVesselUpdated) {
      localStorage.setItem('onlyPSVDataUpdatedForVessel', vesselEquipmentId);
    }

    if (isOnlyPSVDataUpdatedForVesselId && isOnlyPSVDataUpdatedForVessel) {
      localStorage.setItem('onlyPSVDataUpdatedForVessel', isOnlyPSVDataUpdatedForVesselId);
    }
    navigate(isNewEquipment ? -2 : -1);
    dirtyRef.current = {
      isDirty: false,
      dirtyFields: {}
    };

    setSaveInProgress(false);
    setResetEquipmentModal(false);
    setResetNewAndAssignedEquipmentLoading(false);
  };

  const revertAllNewAndAssignedPSVs = async () => {
    if (watch('psv')?.length > 0) {
      const { updatesPSVs, deleteNewPSVsIds, linkPSVsIds } = revertPSVs({
        psvs: watch('psv'),
        equipmentId: equipmentId || newEquipmentId
      });

      console.log({ updatesPSVs, deleteNewPSVsIds, linkPSVsIds });

      if (updatesPSVs?.length > 0) {
        for (let cat of updatesPSVs) {
          const { transform: transform1, transformJurisdiction: transformJurisdiction1 } = cat;
          dispatch?.(
            cacheActions.updateExistingEquipments({
              equipmentId: cat?.equipmentId,
              ...transform1
              // equipmentEditingStatus: '',
              // verificationStatus: ''
            })
          );
          //   if (transformJurisdiction1)
          //     dispatch?.(
          //       jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
          //         ...transformJurisdiction1
          //       })
          //     );
        }
      }

      if (linkPSVsIds?.length > 0) {
        const { pvPsv } = store.getState();
        let copyPvPsv = [...pvPsv.pvPsv] || [];
        copyPvPsv = copyPvPsv?.filter(
          (x) =>
            !(
              x?.isNew &&
              (x?.pvId === equipmentId || x?.pvId === newEquipmentId) &&
              linkPSVsIds.includes(x?.psvId)
            )
        );

        const pvPsvMap = copyPvPsv?.reduce((acc, { pvId, psvId }) => {
          if (!acc[pvId]) acc[pvId] = { pv: [], psv: [] }; // for uniqueness
          if (!acc[psvId]) acc[psvId] = { pv: [], psv: [] }; // for uniqueness
          acc[pvId].psv.push(psvId);
          acc[psvId].pv.push(pvId);
          return acc;
        }, {});

        dispatch?.(pvPsvActions.setPVPSV({ pvPsv: copyPvPsv, pvPsvMap }));
      }

      if (deleteNewPSVsIds?.length > 0) {
        const equipments = store.getState()?.cache?.equipments || [];

        let copyEquipments = [...equipments];
        copyEquipments = copyEquipments?.filter((x) => !deleteNewPSVsIds?.includes(x?.equipmentId));
        const res = await equipmentMobileData({
          equipmentPayloadData: copyEquipments,
          isCheckOldAndNewArray: false
        });
        dispatch?.(cacheActions.setEquipments(res));
      }
    } else if (equipmentDetails?.isLinkToExistingPSV || equipmentDetails?.isNew) {
      const { pvPsv } = store.getState();
      let copyPvPsv = [...pvPsv.pvPsv] || [];
      let _copyPvPsv = copyPvPsv;

      copyPvPsv = vesselEquipmentId
        ? copyPvPsv?.filter(
            (x) =>
              !(
                x?.isNew &&
                (x?.psvId === equipmentId || x?.psvId === newEquipmentId) &&
                x.pvId === vesselEquipmentId
              )
          )
        : copyPvPsv?.filter(
            (x) => !(x?.isNew && (x?.psvId === equipmentId || x?.psvId === newEquipmentId))
          );

      if (copyPvPsv?.length !== _copyPvPsv?.length) {
        const pvPsvMap = copyPvPsv?.reduce((acc, { pvId, psvId }) => {
          if (!acc[pvId]) acc[pvId] = { pv: [], psv: [] }; // for uniqueness
          if (!acc[psvId]) acc[psvId] = { pv: [], psv: [] }; // for uniqueness
          acc[pvId].psv.push(psvId);
          acc[psvId].pv.push(pvId);
          return acc;
        }, {});

        dispatch?.(pvPsvActions.setPVPSV({ pvPsv: copyPvPsv, pvPsvMap }));
      }
    }
  };

  const updateAssignedEquipment = async ({ data, equipmentId, status, actionComeFromButton }) => {
    try {
      const { transform, newJurisdictionNumber } = transformEditEquipmentDataForUpdate({
        data,
        equipmentId
      });

      let transformCopy = _.cloneDeep(transform);

      let pvIds = [...(equipmentDetails?.pvIds || [])];

      const copyMoreInfoOfPSVs = watch('psv') || [];
      if (copyMoreInfoOfPSVs?.length > 0 && localStorage.getItem('onlyPSVDataUpdatedForVessel')) {
        const { cache } = store.getState();
        const { equipmentsMap = {}, equipments: cacheEquipments = [] } = cache;
        for (let psv of copyMoreInfoOfPSVs) {
          const psvEquipmentDetails = cacheEquipments[equipmentsMap[psv.equipmentId]] || {};
          dispatch?.(
            cacheActions.updateExistingEquipments({
              ...psvEquipmentDetails,
              equipmentId: psv.equipmentId,
              updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
              commentForReviewer: psvEquipmentDetails?.commentForReviewer
                ? psvEquipmentDetails?.commentForReviewer
                : commentForReviewer
            })
          );
        }
      }
      if (vesselEquipmentId) {
        const findIndex = pvIds?.findIndex((x) => x?.pvId === vesselEquipmentId);

        if (findIndex >= 0) {
          pvIds[findIndex] = {
            pvId: vesselEquipmentId,
            psvId: equipmentId,
            verificationStatus:
              pvIds[findIndex]?.verificationStatus === 'attached'
                ? 'edited'
                : pvIds[findIndex]?.verificationStatus
          };
        } else {
          pvIds.push({
            pvId: vesselEquipmentId,
            psvId: equipmentId,
            verificationStatus: 'edited'
          });
        }
      }
      dispatch?.(
        cacheActions.updateExistingEquipments({
          equipmentId: equipmentId,
          equipmentEditingStatus: status,
          updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
          ...transformCopy,
          pvIds
        })
      );
      localStorage.setItem('equipmentDetailsUpdated', equipmentId);
      if (newJurisdictionNumber)
        dispatch?.(
          jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
            ...newJurisdictionNumber
          })
        );
      if (actionComeFromButton) {
        localStorage.removeItem('equipmentDetailsUpdated');
        localStorage.removeItem('onlyPSVDataUpdatedForVessel');
        if (vesselEquipmentId && isVesselUpdated) {
          localStorage.setItem('equipmentDetailsUpdated', vesselEquipmentId);
        } else if (vesselEquipmentId && !isVesselUpdated) {
          localStorage.setItem('onlyPSVDataUpdatedForVessel', vesselEquipmentId);
        }

        if (isOnlyPSVDataUpdatedForVesselId && isOnlyPSVDataUpdatedForVessel) {
          localStorage.setItem('onlyPSVDataUpdatedForVessel', isOnlyPSVDataUpdatedForVesselId);
        }
        ToasterUtil.showSuccess(`Record saved as ${status}`);
        navigate(-1);
        dirtyRef.current = {
          isDirty: false,
          dirtyFields: {}
        };
        setSaveInProgress(false);
        setResetEquipmentModal(false);
        setResetNewAndAssignedEquipmentLoading(false);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit(async (data) => {
      setDisabledSaveBtn(true);
      onSubmitHandler({
        data: {
          ...data,
          commentForReviewer:
            siteId || equipmentDetails?.isNew
              ? data?.commentForReviewer
              : localStorage.getItem('equipmentDetailsUpdated') && commentForReviewer
              ? commentForReviewer
              : ''
        },
        status: 'Pending sync'
      });
    })();
  };

  const watchedValues = watch();
  const moveOn = useMemo(() => {
    return isDirty && Object.values(dirtyFields)?.length > 0 ? true : false;
  }, [isDirty, dirtyFields]);

  useEffect(() => {
    if (moveOn) {
      const handler = setTimeout(() => {
        onSubmitHandler({
          data: {
            ...watch(),
            commentForReviewer:
              siteId || equipmentDetails?.isNew
                ? watch('commentForReviewer')
                : localStorage.getItem('equipmentDetailsUpdated') && commentForReviewer
                ? commentForReviewer
                : ''
          },
          status: equipmentDetails?.equipmentEditingStatus
            ? equipmentDetails?.equipmentEditingStatus === 'Pending sync'
              ? equipmentDetails?.equipmentEditingStatus
              : 'In progress'
            : 'In progress',
          actionComeFromButton: false
        });
      }, 900); // 900ms debounce time
      return () => {
        clearTimeout(handler);
      };
    }
  }, [moveOn, watchedValues]);

  const onSaveEquipmentEvent = async () => {
    const res = await trigger();

    if (!res) {
      setSaveInProgress(false);
      return;
    } else {
      onSubmitHandler({
        data: {
          ...watch(),
          commentForReviewer:
            siteId || equipmentDetails?.isNew
              ? watch('commentForReviewer')
              : localStorage.getItem('equipmentDetailsUpdated') && commentForReviewer
              ? commentForReviewer
              : ''
        },
        status: 'Pending sync'
      });
    }
  };

  const onDiscardEquipmentEvent = () => {
    onSubmitHandler({ data: {}, status: '', revert: true });
  };

  const closedModal = () => {
    if (vesselEquipmentId && isVesselUpdated) {
      localStorage.setItem('equipmentDetailsUpdated', vesselEquipmentId);
    } else if (
      vesselEquipmentId &&
      !isVesselUpdated &&
      localStorage.getItem('equipmentDetailsUpdated')
    ) {
      localStorage.setItem('onlyPSVDataUpdatedForVessel', vesselEquipmentId);
    }

    if (isOnlyPSVDataUpdatedForVesselId && isOnlyPSVDataUpdatedForVessel) {
      localStorage.setItem('onlyPSVDataUpdatedForVessel', isOnlyPSVDataUpdatedForVesselId);
    }

    if (!vesselEquipmentId) {
      localStorage.removeItem('onlyPSVDataUpdatedForVessel');
      localStorage.removeItem('equipmentDetailsUpdated');
    }
    navigate(-1);

    dirtyRef.current = {
      isDirty: false,
      dirtyFields: {}
    };
  };

  const handleDiscardEquipmentEvent = () => {
    onSubmitHandler({ data: {}, status: '', revert: true });
  };

  useEffect(() => {
    if (!openLinkToExistingPSVsModal) {
      document.body.classList.remove('filter-open');
    }
  }, [openLinkToExistingPSVsModal]);

  const selectedPSVEquipmentEvent = ({ id }) => {
    const { picklists, cache, jurisdictionNumbers, settings } = store.getState();
    const { picklistsMap = {} } = picklists;
    const { equipmentsMap = {}, equipments: cacheEquipments = [] } = cache;
    const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;
    const { pvPsv, pvPsvMap, alreadyExists } = attachedNewPSVToVesselEquipment({
      equipmentId: equipmentId || newEquipmentId,
      attachedPSVId: id
    });
    // localStorage.setItem('equipmentDetailsUpdated', equipmentId || newEquipmentId);
    localStorage.setItem('onlyPSVDataUpdatedForVessel', equipmentId || newEquipmentId);
    if (!alreadyExists) {
      dispatch?.(pvPsvActions.setPVPSV({ pvPsv, pvPsvMap }));
      const psvEquipmentDetails = cacheEquipments[equipmentsMap[id]] || {};
      const psvDetails = setDefaultValuesOnAddOrEditEquipmentForm({
        equipmentDetails: psvEquipmentDetails,
        jurisdictionNumbersMap,
        picklistsMap,
        isPSVs: true,
        pvId: equipmentId || newEquipmentId
      });

      const equipmentDisplayFields = settings.settings?.['equipmentDisplayFields'] || '';
      const { jurisdictionNumber } = jurisdictionNumbersMap[psvEquipmentDetails.equipmentId] || {};

      const dataPoints = getDataPoints(
        {
          ...psvEquipmentDetails,
          subtype: psvEquipmentDetails?.equipmentSubType,
          jurisdictionNumber
        },
        equipmentDisplayFields,
        'equipment',
        picklistsMap
      );

      const verificationStatus = psvEquipmentDetails?.isNew ? 'new' : 'attached';

      const pvIds = [...(psvEquipmentDetails?.pvIds || [])];
      pvIds.push({
        pvId: equipmentId || newEquipmentId,
        psvId: id,
        // isDetached: 'attached'
        verificationStatus: 'attached'
      });

      dispatch?.(
        cacheActions.updateExistingEquipments({
          equipmentId: id,
          equipmentEditingStatus: psvEquipmentDetails?.equipmentEditingStatus
            ? psvEquipmentDetails?.equipmentEditingStatus
            : 'Pending sync',
          updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
          isLinkToExistingPSV: true,
          pvIds,
          commentForReviewer: psvEquipmentDetails?.commentForReviewer
            ? psvEquipmentDetails?.commentForReviewer
            : commentForReviewer
        })
      );
      const copyMoreInfoOfPSVs = watch('psv') || [];
      copyMoreInfoOfPSVs.push({ ...psvDetails, dataPoints, isLinkToExistingPSV: true });
      setValue('psv', copyMoreInfoOfPSVs, { shouldDirty: false });
    }
    setOpenLinkToExistingPSVsModal(false);
    ToasterUtil.showSuccess(`Record saved as Pending sync`);
  };

  const moreInfoOfPSVs = useMemo(() => {
    const psvs = watch('psv') || [];
    const moreInfoOfPSVs =
      psvs?.length > 0
        ? psvs?.map((a) => {
            const category = a?.equipmentCategoryValue || null;
            const type = a?.equipmentTypeValue || null;

            return {
              id: a?.equipmentId,
              title: `${category} - ${type}`,
              name: a?.name || null,
              serial: a?.serial || null,
              link: a?.link || null,
              verificationStatus: a?.verificationStatus || '',
              equipmentEditingStatus: a?.equipmentEditingStatus || '',
              isDetached:
                a?.verificationStatus === 'attached' ||
                a?.verificationStatus === 'edited' ||
                a?.verificationStatus === 'new'
                  ? true
                  : false,
              dataPoints: a.dataPoints || [],
              isLinkToExistingPSV: a?.isLinkToExistingPSV
            };
          })
        : [];
    return moreInfoOfPSVs;
  }, [watch('psv')]);

  const moreInfoOfVessel = useMemo(() => {
    return watch('vessel')?.length > 0 ? watch('vessel') : [];
  }, [watch('vessel')]);

  const openAttachOrReattachModalEvent = ({ isDetached, equipmentId }) => {
    setOpenAttachOrReattachModal(true);
    setIsDetachedAndEquipmentId({ isDetached, equipmentId });
  };

  const deAttachedOrAttachedSelectedPSV = ({ deAttached, reattach, equipmentId, pvId }) => {
    try {
      setLoading(true);

      const { cache } = store.getState();
      const { equipmentsMap = {}, equipments: cacheEquipments = [] } = cache;
      const psvEquipmentDetails = cacheEquipments[equipmentsMap[equipmentId]] || {};
      const verificationStatus = reattach ? 'attached' : 'detached';
      const equipmentEditingStatus = psvEquipmentDetails?.equipmentEditingStatus || 'Pending sync';

      let pvIds = [...(psvEquipmentDetails?.pvIds || [])];
      let cat = pvIds?.findIndex((x) => x?.pvId === pvId);

      if (cat >= 0) {
        pvIds[cat] = {
          pvId: pvId,
          psvId: equipmentId,
          verificationStatus
        };
      } else {
        pvIds.push({
          pvId: pvId,
          psvId: equipmentId,
          verificationStatus
        });
      }

      dispatch?.(
        cacheActions.updateExistingEquipments({
          ...psvEquipmentDetails,
          equipmentId: equipmentId,
          updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
          verificationStatus,
          equipmentEditingStatus,
          pvIds,
          commentForReviewer: psvEquipmentDetails?.commentForReviewer
            ? psvEquipmentDetails?.commentForReviewer
            : commentForReviewer
        })
      );

      let psvs = watch('psv') || [];
      const findIndex = psvs?.findIndex((x) => x.equipmentId === equipmentId);
      if (findIndex > -1) {
        psvs[findIndex].verificationStatus = verificationStatus;
        psvs[findIndex].equipmentEditingStatus = equipmentEditingStatus;
        psvs[findIndex].pvIds = pvIds;
        localStorage.setItem('onlyPSVDataUpdatedForVessel', true);
        // setValue('psv', psvs, { shouldDirty: true });
        setValue('psv', psvs, { shouldDirty: false });
        localStorage.setItem('onlyPSVDataUpdatedForVessel', equipmentId || newEquipmentId);
      }
    } catch (error) {
      console.log({ error });
    }
    setOpenAttachOrReattachModal(false);
    setIsDetachedAndEquipmentId({
      isDetached: false,
      equipmentId: null
    });
    setLoading(false);
    ToasterUtil.showSuccess(`Record saved as Pending sync`);
  };

  const isNewEquipmentIsVesselVersion = useMemo(() => {
    if (equipmentCategoryMap && !equipmentIdThatAttachedToPSV) {
      const { inspection } = store.getState();
      const equipmentCategoryTypeSubtype = inspection.equipmentCategoryTypeSubtype || [];

      const equipmentCategoryKeys = getParentKeys(
        equipmentCategoryTypeSubtype,
        watch('equipmentCategoryTypeSubType')
      );
      const equipmentCategory = equipmentCategoryKeys[0] || '';

      const { isVesselVersion, isPSVVersion } = getEquipmentTypeVersion({
        equipmentCategoryMap,
        equipmentDetails: {
          equipmentCategory
        }
      });

      return isVesselVersion;
    }
    return false;
  }, [
    newEquipmentId,
    equipmentCategoryMap,
    watch('equipmentCategoryTypeSubType'),
    equipmentIdThatAttachedToPSV
  ]);

  const setOpenAddFieldModalEvent = ({ value }) => {
    setOpenAddFieldModal(value);
  };

  useEffect(() => {
    if (!loading && equipmentData && !newEquipmentId && !equipmentDetails?.moreFields) {
      const selectedMoreFields = equipmentData?.initialFormFieldsValue?.selectedMoreFields || [];
      const newSelectedMoreFields = [];
      for (let field of selectedMoreFields) {
        const sourceEntity = field.sourceEntity
          ? equipmentAutoPopulateFields[field.sourceEntity] || null
          : null;
        const sourceValue =
          sourceEntity && field.sourceProperty ? sourceEntity[field.sourceProperty] || null : null;
        const type = field?.type;
        if (
          sourceValue &&
          [
            'YN',
            'Date_Nullable',
            'Date',
            'Dropdown',
            'Dropdown_PL',
            'LongText',
            'Text',
            'PhoneNumber',
            'Numeric'
          ].includes(type) &&
          !field?.isFieldCheckedAndDisabled
        ) {
          newSelectedMoreFields.push({
            ...field,
            checked: true,
            isVisible: true
          });
          setValue(field?.name, sourceValue);
          setValue(`${field?.name}-reconcile`, sourceValue);
        } else if (
          (sourceValue?.textBoxValue || sourceValue?.textBoxUnitValue) &&
          ['Readings', 'Readings_PL'].includes(type) &&
          !field?.isFieldCheckedAndDisabled
        ) {
          newSelectedMoreFields.push({
            ...field,
            checked: true,
            isVisible: true
          });
          setValue(field?.name, sourceValue);
          setValue(`${field?.name}-reconcile`, sourceValue);
        } else {
          newSelectedMoreFields.push({
            ...field
          });
        }
      }
      setValue('selectedMoreFields', newSelectedMoreFields);
    }
  }, [equipmentAutoPopulateFields, loading, equipmentData, newEquipmentId, equipmentDetails]);

  const selectedMoreFieldEvent = (values) => {
    const _selectedMoreFields = [...(watch('selectedMoreFields') || [])];
    let _moreFields = { ...(watch('moreFields') || {}) };
    const newSelectedMoreFields = [];

    const checkedFieldIds = _selectedMoreFields?.filter((x) => x?.checked)?.map((y) => y?.Id) || [];
    let newSelectedFieldIds = values?.filter((x) => !checkedFieldIds.includes(x));

    for (let field of _selectedMoreFields) {
      if (newSelectedFieldIds.includes(field?.Id)) {
        const sourceEntity = field.sourceEntity
          ? equipmentAutoPopulateFields[field.sourceEntity] || null
          : null;
        const sourceValue =
          sourceEntity && field.sourceProperty ? sourceEntity[field.sourceProperty] || null : null;

        newSelectedMoreFields.push({
          ...field,
          checked: true,
          isVisible: true
        });
        setValue(field?.name, sourceValue, { shouldDirty: true });
        setValue(`${field?.name}-reconcile`, sourceValue, { shouldDirty: true });
      } else if (!values.includes(field?.Id) && field.checked) {
        const name = field?.fieldName;
        delete _moreFields[name];
        setValue('moreFields', _moreFields);

        newSelectedMoreFields.push({
          ...field,
          checked: false,
          isVisible: false
        });
      } else {
        newSelectedMoreFields.push(field);
      }
    }
    setValue('selectedMoreFields', newSelectedMoreFields, { shouldDirty: true });
  };

  const { addFieldsList, moreInfoFormFields } = useMemo(() => {
    let addFieldsList = watch('selectedMoreFields') || [];
    const moreInfoFormFields = addFieldsList?.filter((x) => x?.isVisible);
    return { addFieldsList, moreInfoFormFields };
  }, [watch('selectedMoreFields')]);

  const onClickOkEvent = () => {
    const onlyPSVDataUpdatedForVessel =
      localStorage.getItem('onlyPSVDataUpdatedForVessel') || false;

    const isEquipmentDetailsUpdated = localStorage.getItem('equipmentDetailsUpdated') || false;

    if (!isEquipmentDetailsUpdated && onlyPSVDataUpdatedForVessel) {
      const copyMoreInfoOfPSVs = watch('psv') || [];
      if (copyMoreInfoOfPSVs?.length > 0 && localStorage.getItem('onlyPSVDataUpdatedForVessel')) {
        const { cache } = store.getState();
        const { equipmentsMap = {}, equipments: cacheEquipments = [] } = cache;
        for (let psv of copyMoreInfoOfPSVs) {
          const psvEquipmentDetails = cacheEquipments[equipmentsMap[psv.equipmentId]] || {};
          dispatch?.(
            cacheActions.updateExistingEquipments({
              ...psvEquipmentDetails,
              equipmentId: psv.equipmentId,
              updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
              commentForReviewer: psvEquipmentDetails?.commentForReviewer
                ? psvEquipmentDetails?.commentForReviewer
                : commentForReviewer
            })
          );
        }
      }

      setShowAttachOrDeAttachPSVModalBeforeSaved(false);
      localStorage.removeItem('onlyPSVDataUpdatedForVessel');

      navigate(-1);
      dirtyRef.current = {
        isDirty: false,
        dirtyFields: {}
      };
    } else if (isEquipmentDetailsUpdated && onlyPSVDataUpdatedForVessel) {
      setShowAttachOrDeAttachPSVModalBeforeSaved(false);
      localStorage.removeItem('onlyPSVDataUpdatedForVessel');

      onSubmitHandler({
        data: {
          ...watch(),
          commentForReviewer:
            siteId || equipmentDetails?.isNew
              ? watch('commentForReviewer')
              : localStorage.getItem('equipmentDetailsUpdated') && commentForReviewer
              ? commentForReviewer
              : ''
        },
        status: 'Pending sync',
        actionComeFromButton: true
      });
    }
  };

  const showToasterOfAddingOnlyCommentsCOmments = () => {
    setShowNotifications(true);
    setTimeout(() => {
      setShowNotifications(false);
    }, 3000);
  };

  return (
    <div
      style={{
        position: 'relative'
      }}
    >
      <>
        <DisabledLocation
          showLocationDisabledInfo={showNotifications}
          text={'Equipment data must be changed before you can save an edited asset record.'}
          className={style.showCOmmentToaster}
        />

        <form onSubmit={onSubmit} id="addOrEditEquipmentContainer">
          <div className={style.modalHeight}>
            <Header
              title={
                equipmentHeaderTitle
                  ? equipmentHeaderTitle
                  : isEditable
                  ? 'Edit equipment'
                  : 'Add equipment'
              }
              closedModalEvent={closedModal}
              setSaveInProgressEvent={() => {
                setSaveInProgress(true);
              }}
              moveOn={moveOn || localStorage.getItem('equipmentDetailsUpdated')}
            />

            {loading ? (
              <div className={style.loaderContainer}>
                <LoadingRound className={style.loaderInnerContainer} />
              </div>
            ) : (
              <BodyContainer
                errors={errors}
                register={register}
                watch={watch}
                getValues={getValues}
                control={control}
                setValue={setValue}
                siteDetail={siteDetail}
                formFields={formFields}
                moreInfoFormFields={moreInfoFormFields}
                moreInfoOfVessel={moreInfoOfVessel}
                moreInfoOfPSVs={moreInfoOfPSVs}
                isNewEquipment={siteId || equipmentDetails?.isNew}
                isVesselVersion={isNewEquipmentIsVesselVersion}
                isPSVVersion={isPSVVersion}
                setOpenAddPsvModalEvent={() => {
                  setOpenAddPsvModal(true);
                }}
                newEquipmentId={newEquipmentId}
                openAttachOrReattachModalEvent={openAttachOrReattachModalEvent}
                setOpenAddFieldModalEvent={setOpenAddFieldModalEvent}
                attachAndDeAttach={attachAndDeAttach}
                equipmentEditingStatus={equipmentDetails?.equipmentEditingStatus || ''}
                commentForReviewer={commentForReviewer}
                setCommentForReviewer={setCommentForReviewer}
              />
            )}
          </div>

          <Footer
            moveOn={moveOn}
            showDeleteBtn={showDeleteBtn}
            setResetEquipmentEvent={() => {
              setResetEquipmentModal(true);
            }}
            commentForReviewer={commentForReviewer}
            showToasterOfAddingOnlyCommentsCOmments={showToasterOfAddingOnlyCommentsCOmments}
            vesselEquipmentId={vesselEquipmentId}
            isVesselUpdated={isVesselUpdated}
          />
        </form>
      </>

      {saveInProgress && (
        <SaveModal
          open={saveInProgress}
          setOpen={(value) => {
            setSaveInProgress(value);
          }}
          saveChanges={true}
          hideDiscardButton={showDeleteBtn ? true : false}
          onDiscard={onDiscardEquipmentEvent}
          onSave={onSaveEquipmentEvent}
        />
      )}

      {resetEquipmentModel && (
        <DeleteModal
          open={resetEquipmentModel}
          setOpen={setResetEquipmentModal}
          discordChangesAndResetAsset
          recordType={'equipment'}
          handleDelete={() => {
            handleDiscardEquipmentEvent();
          }}
          disabledDiscordChangesAndResetAsset={resetNewAndAssignedEquipmentLoading}
        />
      )}

      {openAddPsvModal && (
        <AddPSVModal
          open={openAddPsvModal}
          setOpen={setOpenAddPsvModal}
          addNewPSV
          addNewPSVEvent={() => {
            const vesselEquipmentId = equipmentId || newEquipmentId;
            const isVesselUpdated =
              localStorage.getItem('equipmentDetailsUpdated') === vesselEquipmentId
                ? 'true'
                : 'false';

            const isOnlyPSVDataUpdatedForVessel = localStorage.getItem(
              'onlyPSVDataUpdatedForVessel'
            )
              ? 'true'
              : 'false';

            const title1 = 'Add PSV';
            let url = `/add-edit-equipment?new-equipmentId=${uuidv4()}&siteId=${
              equipmentDetails?.siteId || siteId
            }&title=${title1}&equipmentIdThatAttachedToPSV=${vesselEquipmentId}&vesselEquipmentId=${vesselEquipmentId}&isVesselUpdated=${isVesselUpdated}&isOnlyPSVDataUpdatedForVessel=${isOnlyPSVDataUpdatedForVessel}`;

            const isOnlyPSVDataUpdatedForVesselId =
              localStorage.getItem('onlyPSVDataUpdatedForVessel') || null;

            if (isOnlyPSVDataUpdatedForVesselId) {
              url = `${url}&isOnlyPSVDataUpdatedForVesselId=${localStorage
                .getItem('onlyPSVDataUpdatedForVessel')
                .toString()}`;
            }

            setOpenAddPsvModal(false);
            setTimeout(() => {
              navigate(url);
            }, 100);
          }}
          linkToExistingPSVsEvent={() => {
            setOpenLinkToExistingPSVsModal(true);
            setOpenAddPsvModal(false);
          }}
        />
      )}

      {openAttachOrReattachModal && (
        <AttachPSVModal
          open={openAttachOrReattachModal}
          setOpen={setOpenAttachOrReattachModal}
          deAttached={isDetachedAndEquipmentId?.isDetached}
          attached={!isDetachedAndEquipmentId?.isDetached}
          deAttachedHandleEvent={() => {
            deAttachedOrAttachedSelectedPSV({
              deAttached: true,
              reattach: false,
              equipmentId: isDetachedAndEquipmentId?.equipmentId,
              pvId: equipmentId || newEquipmentId
            });
          }}
          unlinkHandleEvent={() => {
            deAttachedOrAttachedSelectedPSV({
              deAttached: false,
              reattach: true,
              equipmentId: isDetachedAndEquipmentId?.equipmentId,
              pvId: equipmentId || newEquipmentId
            });
          }}
        />
      )}
      {openLinkToExistingPSVsModal && (
        <PSVEquipmentModal
          closedModal={() => setOpenLinkToExistingPSVsModal(false)}
          open={openLinkToExistingPSVsModal}
          selectedPSVEquipmentEvent={selectedPSVEquipmentEvent}
          alreadyAttachedPSV={moreInfoOfPSVs?.map((x) => x?.id)}
          siteId={equipmentDetails?.siteId || siteId}
        />
      )}
      {openAddFieldModal && addFieldsList?.length > 0 && (
        <AddFieldsModal
          isEquipment
          text={'structure'}
          openModal={openAddFieldModal}
          onSubmit={selectedMoreFieldEvent}
          setOpenModal={(value) => setOpenAddFieldModalEvent({ value })}
          fields={addFieldsList}
        />
      )}

      {showAttachOrDeAttachPSVModalBeforeSaved && (
        <AddPSVModal
          open={showAttachOrDeAttachPSVModalBeforeSaved}
          setOpen={setShowAttachOrDeAttachPSVModalBeforeSaved}
          changingPSVVesselRelationship
          onClickOkEvent={() => {
            onClickOkEvent();
          }}
          isChangingPSVVesselRelationshipEvent={({ value }) => {
            dispatch?.(settingsActions.setIsChangingPSVVesselRelationship(value));
            localStorage.setItem('isChangingPSVVesselRelationship', value);
          }}
          showPSVVesselGrayBox={!localStorage.getItem('equipmentDetailsUpdated')}
          isChangingPSVVesselRelationship={
            store.getState()?.settings?.isChangingPSVVesselRelationship || false
          }
        />
      )}
    </div>
  );
};

export default AddOrEditEquipment;
